import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { Environment, ServerResult } from '@app/shared/models';
import { APP_ENVIRONMENT } from '@app/shared/tokens';

import {
    Credentials,
    CurrentUser,
    LinkInfo,
    TokenResult,
    UpdatePasswordModel,
    UserProfile,
    VisitorRequestModel,
} from '../models';
import { errorHandler, getData, verifyServerResult } from '../operators';

export interface GetTokenModel {
    [key: string]: string | boolean;
    password: string;
    firstName: string;
    lastName: string;
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    env: Environment = inject(APP_ENVIRONMENT);
    http = inject(HttpClient);

    signIn(credentials: Credentials): Observable<TokenResult> {
        return this.http
            .post<ServerResult<TokenResult>>(`${this.env.proxyServerUrl}/auth/sign-in`, credentials)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    signOut(): Observable<void> {
        return this.http
            .get<ServerResult<void>>(`${this.env.proxyServerUrl}/auth/sign-out`)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    getCurrentUser(): Observable<CurrentUser> {
        return this.http
            .post<ServerResult<CurrentUser>>(`${this.env.proxyServerUrl}/users/current`, null)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    saveUserProfile(profile: UserProfile): Observable<UserProfile> {
        return this.http
            .post<ServerResult<UserProfile>>(`${this.env.serverUrl}/users/current/profile`, profile)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    changePassword(model: UpdatePasswordModel): Observable<void> {
        return this.http
            .post<ServerResult<void>>(`${this.env.serverUrl}/users/current/update-password`, model)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    updateCookie(): Observable<void> {
        return this.http
            .post<ServerResult<void>>(`${this.env.proxyServerUrl}/auth/token/update-cookie`, null)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    verifyRequest(visitorId: string, requestId: string): Observable<VisitorRequestModel> {
        return this.http.get<VisitorRequestModel>(`${this.env.fpEndpointUrl}/worker/verify/request`, {
            params: { visitorId, requestId },
        });
    }

    getCurrentToken(): Observable<TokenResult> {
        return this.http
            .post<ServerResult<TokenResult>>(`${this.env.proxyServerUrl}/auth/token/me`, null)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    cookieTest(id?: string): Observable<any> {
        return this.http
            .post<ServerResult<any>>(`${this.env.proxyServerUrl}/ct`, { id })
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    setupDevice(visitorId: string, isIncognito: boolean): Observable<any> {
        return this.http
            .post<ServerResult<any>>(`${this.env.proxyServerUrl}/auth/setup-device`, {
                visitorId,
                isIncognito,
                uuid: performance.now().toString(),
                timestamp: new Date(),
            })
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    heartBeat(): Observable<void> {
        return this.http
            .get<ServerResult<void>>(`${this.env.rapidApiUrl}/heart-beat`)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    activateSession(trackLogin: boolean): Observable<TokenResult> {
        return this.http
            .post<ServerResult<TokenResult>>(
                `${this.env.proxyServerUrl}/link/current/activate`,
                trackLogin ? 'true' : 'false',
            )
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    refreshToken(): Observable<TokenResult> {
        return this.http
            .post<ServerResult<TokenResult>>(`${this.env.proxyServerUrl}/auth/token/refresh`, null)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    sendDiagnosticData(data: any): Observable<string> {
        return this.http
            .post<ServerResult<string>>(`${this.env.proxyServerUrl}/diagnostic`, JSON.stringify(data))
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    getLinkInfo(): Observable<LinkInfo> {
        return this.http
            .get<ServerResult<LinkInfo>>(`${this.env.proxyServerUrl}/link/info`)
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    getTokenForLink(
        model: GetTokenModel,
        thumbprint: string,
        requestId: string,
        newSession = false,
    ): Observable<TokenResult> {
        const options = { headers: {} };

        if (newSession) {
            options.headers = { 'x-new-session': 'true' };
        }

        return this.http
            .post<ServerResult<TokenResult>>(
                `${this.env.proxyServerUrl}/link/get-token`,
                { ...model, thumbprint, requestId },
                options,
            )
            .pipe(verifyServerResult(), getData(), errorHandler());
    }

    requestMoreTime(): Observable<void> {
        return this.http
            .post<ServerResult<void>>(`${this.env.proxyServerUrl}/link/request-more-time`, {})
            .pipe(verifyServerResult(), getData(), errorHandler());
    }
}
